import { type FC, type FormEvent, useMemo, useState } from "react";

import { joinWaitlistAsPublic } from "../../dashboard-client";

const SUCCESS_ICON_PATH =
  "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z";
const ERROR_ICON_PATH =
  "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z";

const NoticeBanner: FC<{
  iconPath: string;
  children: string;
  tone: "error" | "success";
}> = ({ iconPath, children, tone }) => {
  const toneClassName =
    tone === "success"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
      : "border-rose-500/30 bg-rose-500/10 text-rose-400";

  return (
    <div
      className={`flex items-start gap-2.5 rounded-none border px-3.5 py-3 font-mono text-sm ${toneClassName}`}
    >
      <svg
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path fillRule="evenodd" d={iconPath} clipRule="evenodd" />
      </svg>
      {children}
    </div>
  );
};

export const WaitlistCard: FC = () => {
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = useMemo(
    () => email.trim().length > 3 && email.includes("@"),
    [email],
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit || loading) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await joinWaitlistAsPublic({
        body: { company: company.trim() || undefined, email: email.trim() },
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong - try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="border-2 border-slate-700 bg-arti-panel p-6">
        <div className="mb-4 inline-block rounded-none border-2 border-emerald-500 px-2 py-1">
          <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-emerald-400">
            ■ Waitlist
          </span>
        </div>

        <NoticeBanner iconPath={SUCCESS_ICON_PATH} tone="success">
          You're on the list. We'll email you when a seat opens.
        </NoticeBanner>

        <div className="mt-4 font-mono text-[11px] uppercase tracking-wider text-slate-500">
          [ Waitlist · Confirmed ]
        </div>
      </div>
    );
  }

  return (
    <div className="border-2 border-slate-700 bg-arti-panel p-6">
      <div className="mb-4 inline-block rounded-none border-2 border-emerald-500 px-2 py-1">
        <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-emerald-400">
          ■ Waitlist
        </span>
      </div>

      <h2 className="mb-2 text-2xl font-semibold normal-case !tracking-tight text-white">
        Join the waitlist
      </h2>

      <p className="mb-6 text-base text-slate-400">
        We're onboarding teams in small batches. Leave your work email and we'll
        reach out when your seat opens.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label
            htmlFor="waitlist-email"
            className="block font-mono text-[11px] uppercase tracking-wider text-slate-500"
          >
            Work email
          </label>
          <input
            id="waitlist-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            className="mt-2 w-full rounded-none border-2 border-slate-700 bg-arti-background px-4 py-3 font-mono text-sm text-slate-100 outline-none transition focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 placeholder:text-slate-600"
            disabled={loading}
            required
          />
        </div>

        <div className="mb-6">
          <label
            htmlFor="waitlist-company"
            className="block font-mono text-[11px] uppercase tracking-wider text-slate-500"
          >
            Company · optional
          </label>
          <input
            id="waitlist-company"
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="ACME Corp"
            className="mt-2 w-full rounded-none border-2 border-slate-700 bg-arti-background px-4 py-3 font-mono text-sm text-slate-100 outline-none transition focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 placeholder:text-slate-600"
            disabled={loading}
          />
        </div>

        {error && (
          <NoticeBanner iconPath={ERROR_ICON_PATH} tone="error">
            {error}
          </NoticeBanner>
        )}

        <button
          type="submit"
          disabled={!canSubmit || loading}
          className="inline-flex w-full cursor-pointer items-center justify-center rounded-none bg-emerald-500 px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-950 transition hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? "Joining..." : "Join the waitlist"}
        </button>
      </form>

      <div className="mt-4 font-mono text-[11px] tracking-wider text-slate-500">
        Already approved?{" "}
        <a href="/login" className="text-emerald-400 hover:text-emerald-300">
          Sign in
        </a>
      </div>
    </div>
  );
};
